import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router";
import { useModal } from "../../SharedModule/hooks/useModal";
import { useUpdateInvoices } from "../hooks/useUpdateInvoices";
import { useChangeOwner } from "../hooks/useChangeOwner";
import { SelectUsers } from "../redux/reducers/metaBilling.reducer";
import { BillingService } from "../services/billing";
import { Invoice as InvoiceType } from "../../SharedModule/types/shared-module-types";
import { Detail } from "../components/InvoiceDetailPage/InvoiceDetail";
import { ActivityAndComments } from "../components/InvoiceDetailPage/ActivityAndComments";
import { useSendReminder } from "../hooks/useSendReminder";
import { useBillingMetadata } from "../hooks/useBillingMetadata";
import { useIsAllowed } from "../../SharedModule/components/AccessControl";
import {
  ROLE_NOT_OWNERS,
  ROUTE_NOT_FOUND_PAGE,
} from "../../SharedModule/utils/constants";
import { Spinner } from "../../SharedModule/components/Spinner";
import Footer from "../../SharedModule/components/Footer";
import { selectUserRoles } from "../../SharedModule/redux/reducers/auth.reducer";
import { billingModule } from "../../SharedModule/utils/accessUtils";
import { ForbiddenPage } from "../../SharedModule/pages/ForbiddenPage";

export const Invoice = () => {
  useBillingMetadata();
  let { id } = useParams<{ id: any }>();
  const [invoice, setInvoice] = useState<
    (InvoiceType & { selected?: boolean }) | null
  >(null);

  const { userRolesList, rolesLoaded } = useSelector(selectUserRoles);

  const users = useSelector(SelectUsers);
  const navigate = useNavigate();
  const location = useLocation();
  const { showModal, closeModal, Modal, ...modalProps } = useModal();

  const { activeUserId, isAllowed } = useIsAllowed(ROLE_NOT_OWNERS);

  const loadInvoice = useCallback(async () => {
    try {
      const invoice = await BillingService.getInvoice(id, true);
      if (typeof invoice === "object") {
        invoice.activityLogs.reverse();
        setInvoice(invoice);

        // FF has some issue that sometimes not scroll to top on navigate to invoice detail
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 100);
      } else {
        navigate(ROUTE_NOT_FOUND_PAGE);
      }
    } catch (err: any) {
      navigate(ROUTE_NOT_FOUND_PAGE);
    }
  }, [id, activeUserId, navigate, isAllowed]);

  useEffect(() => {
    document.title = "The Hub - Invoice Details";
  }, []);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice, location.pathname]);

  const changeOwner = useChangeOwner(showModal, closeModal, loadInvoice);

  const updateInvoice = useUpdateInvoices(
    showModal,
    closeModal,
    invoice && [invoice]
  );

  const sendReminders = useSendReminder(
    showModal,
    closeModal,
    loadInvoice,
    invoice && [invoice]
  );

  return (
    <>
      {!rolesLoaded && (
        <Spinner
          style={{
            marginLeft: "50%",
            marginTop: "5%",
          }}
        />
      )}

      {rolesLoaded && !billingModule(userRolesList) && <ForbiddenPage />}

      {rolesLoaded && billingModule(userRolesList) && (
        <div className="content">
          {invoice ? (
            invoice.id ? (
              <>
                <Modal closeModal={closeModal} {...modalProps} />
                <Detail
                  invoice={invoice}
                  users={users}
                  updateInvoice={updateInvoice}
                  changeOwner={changeOwner}
                  sendReminders={sendReminders}
                  loadInvoice={loadInvoice}
                  userRolesList={userRolesList}
                />
                <ActivityAndComments
                  invoiceId={invoice.id.toString()}
                  activityLogs={invoice.activityLogs}
                  loadInvoice={loadInvoice}
                />
              </>
            ) : (
              <div>Invoice not found</div>
            )
          ) : (
            <Spinner
              style={{
                marginLeft: "50%",
                marginTop: "5%",
              }}
            />
          )}
          <Footer />
        </div>
      )}
    </>
  );
};
